import Icon from '@ant-design/icons';
import { Menu } from 'antd';
import * as React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import CustomContext from '../../../context/CustomContext';
import { ReactComponent as collegeSvg } from '../../../resources/images/ico-collegeDirectory.svg';
import { ReactComponent as eventsSvg } from '../../../resources/images/ico-events.svg';
import { ReactComponent as usersGroupsSvg } from '../../../resources/images/ico-grid.svg';
import { ReactComponent as leaderboardSvg } from '../../../resources/images/ico-leaderboard.svg';
import { ReactComponent as invoicesSvg } from '../../../resources/images/ico-list.svg';
import { ReactComponent as pacerSvg } from '../../../resources/images/ico-pacer.svg';
import { ReactComponent as playerProfileSvg } from '../../../resources/images/ico-playerProfile.svg';
import { ReactComponent as seasonsSvg } from '../../../resources/images/ico-seasons.svg';
import { ReactComponent as trainingSvg } from '../../../resources/images/ico-training.svg';
import { ReactComponent as travelSvg } from '../../../resources/images/ico-travel.svg';
import ga from '../../../services/GoogleAnalytics';
import rolesService from '../../../services/RolesService';
import ParentMenuComponent from '../Common/ParentMenuComponent/ParentMenuComponent';
import PlayerMenuComponent from '../Common/PlayerMenuComponent/PlayerMenuComponent';

class OrganizationNavigationComponent extends React.Component<Props> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    /*** METHODS ***/

    navigate = (path: string) => {
        this.props.history.push(path);
    };

    callAnalyticsTrainingAppEvent = () => {
        ga.createDefaultEvent('training app', 'training app - redirect to training app page from menu');
    };

    callAnalyticsPacerEvent = () => {
        ga.createDefaultEvent('pacer', 'pacer - redirect to PACER from menu');
    };

    callAnalyticsTravelEvent = () => {
        ga.createDefaultEvent('travel', 'travel - redirect to travel page from menu');
    };

    /*** COMPONENTS ***/

    renderPlayerMenu = (): React.ReactNode | undefined => {
        const { collapsed, playerEditable, organizationIds } = this.props;
        const userId = this.props.userId;
        const sportId = this.props.sportId;
        const familyId = this.props.familyId;

        if (userId && sportId) {
            return (
                <PlayerMenuComponent
                    userId={userId}
                    sportId={sportId}
                    familyId={familyId}
                    organizationIds={organizationIds}
                    divider={true}
                    editable={playerEditable}
                    collapsed={collapsed}
                />
            );
        }
    };

    renderParentMenu = (): React.ReactNode | undefined => {
        const { collapsed, userId, sportId } = this.props;
        if (userId && userId !== this.context.auth?.id && !sportId) {
            return <ParentMenuComponent collapsed={collapsed} userId={userId} />;
        }
    };

    render() {
        const { menu, collapsed } = this.props;
        const { auth } = this.context;

        if (menu) {
            return menu;
        } else {
            return (
                <>
                    {this.renderPlayerMenu()}
                    {this.renderParentMenu()}
                    <Menu.ItemGroup
                        key="organization-dashboard"
                        title={!collapsed && <FormattedMessage id="navigation.organization.dashboard" />}
                    >
                        <Menu.Item
                            key="organization-user-profile"
                            icon={<Icon component={playerProfileSvg} />}
                            onClick={() => this.navigate('/')}
                        >
                            <Link to={`/`}>
                                <FormattedMessage id="navigation.organization.dashboard.profile" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="events"
                            icon={<Icon component={eventsSvg} />}
                            onClick={() => this.navigate('/events')}
                        >
                            <Link to="/events">
                                <FormattedMessage id="navigation.organization.dashboard.events" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="leaderboard"
                            icon={<Icon component={leaderboardSvg} />}
                            onClick={() => this.navigate('/leaderboard')}
                        >
                            <Link to="/leaderboard">
                                <FormattedMessage id="navigation.organization.dashboard.leaderboard" />
                            </Link>
                        </Menu.Item>
                        {this.context.auth?.collegeConnectEnabled && (
                            <Menu.Item
                                key="colleges-public"
                                icon={<Icon component={collegeSvg} />}
                                onClick={() => this.navigate('/colleges')}
                            >
                                <Link to="/colleges">
                                    <FormattedMessage id="navigation.organization.dashboard.colleges" />
                                </Link>
                            </Menu.Item>
                        )}
                        <Menu.Item
                            key="groups"
                            icon={<Icon component={usersGroupsSvg} />}
                            onClick={() => this.navigate(`/groups/affiliates/${this.context.auth?.organizationId}`)}
                        >
                            <Link to={`/groups/affiliates/${this.context.auth?.organizationId}`}>
                                <FormattedMessage id="navigation.admin.dashboard.groups" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="notifications"
                            icon={<Icon component={seasonsSvg} />}
                            onClick={() => this.navigate(`/notifications`)}
                        >
                            <Link to={`/notifications`}>
                                <FormattedMessage id="navigation.admin.admin.notifications" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="invoices"
                            icon={<Icon component={invoicesSvg} />}
                            onClick={() => this.navigate(`/invoices/${this.context.auth?.organizationId}`)}
                            hidden={!rolesService.hasAnyRole(auth, ['ROLE_ORGANIZATION_OWNER'])}
                        >
                            <Link to={`/invoices/${this.context.auth?.organizationId}`}>
                                <FormattedMessage id="navigation.admin.dashboard.invoices" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="training"
                            icon={<Icon component={trainingSvg} />}
                            onClick={this.callAnalyticsTrainingAppEvent}
                        >
                            <a href="https://www.top100sports.com/training-app" target="_blank" rel="noreferrer">
                                <FormattedMessage id="navigation.player.dashboard.training" />
                            </a>
                        </Menu.Item>
                        <Menu.Item
                            key="pacer"
                            icon={<Icon component={pacerSvg} />}
                            onClick={this.callAnalyticsPacerEvent}
                        >
                            <a href="https://top100sports.com/PACER" target="_blank" rel="noreferrer">
                                <FormattedMessage id="navigation.player.dashboard.pacer" />
                            </a>
                        </Menu.Item>
                        <Menu.Item
                            key="travel"
                            icon={<Icon component={travelSvg} />}
                            onClick={this.callAnalyticsTravelEvent}
                        >
                            <a href="https://www.sarecsportstravel.com/champions" target="_blank" rel="noreferrer">
                                <FormattedMessage id="navigation.player.dashboard.travel" />
                            </a>
                        </Menu.Item>
                    </Menu.ItemGroup>
                </>
            );
        }
    }
}
export default injectIntl(withRouter(OrganizationNavigationComponent));

interface Props extends WrappedComponentProps, RouteComponentProps {
    menu?: React.ReactNode;
    collapsed: boolean;
    playerEditable?: boolean;
    userId?: string;
    sportId?: number;
    familyId?: number;
    organizationIds?: string[];
}
